import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { ToastContainer } from '@/components/common/Toast';
import { useToast } from '@/lib/hooks/useToast';
import {
  getEffectiveSchedule, setHomeJob, createOverride, deleteOverride,
  type EffectiveSchedule,
} from '@/lib/api/scheduling';
import { getForecast, weatherIcon, type DayForecast } from '@/lib/api/weather';
import { employeePath } from '@/lib/utils/navPaths';

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Sunday that starts the week containing `d`.
function weekStart(d: Date) {
  const s = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  s.setDate(s.getDate() - s.getDay());
  return s;
}

// Stable per-job accent so the same job reads as the same colour everywhere
const JOB_HUES = [24, 130, 205, 265, 330, 45, 170, 300];
const jobHue = (id: number) => JOB_HUES[id % JOB_HUES.length];

export const SchedulingPage = () => {
  const { toast, toasts, removeToast } = useToast();
  const [cursor, setCursor] = useState(() => weekStart(new Date()));
  const [weeks, setWeeks] = useState<1 | 2>(1); // suite: 1- or 2-week view
  const [data, setData] = useState<EffectiveSchedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null); // `${user_id}_${date}`
  const [repeatUntil, setRepeatUntil] = useState('');
  const [forecast, setForecast] = useState<Map<string, DayForecast>>(new Map());

  const start = ymd(cursor);
  const end = ymd(new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + weeks * 7 - 1));
  const today = ymd(new Date());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getEffectiveSchedule(start, end));
    } catch {
      toast.error('Failed to load schedule.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getForecast().then(list => setForecast(new Map(list.map(f => [f.date, f])))).catch(() => {});
  }, []);

  const shiftWeek = (delta: number) =>
    setCursor(c => new Date(c.getFullYear(), c.getMonth(), c.getDate() + delta * 7));

  const run = async (fn: () => Promise<void>, ok?: string) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); setEditing(null); setRepeatUntil(''); await load(); }
    catch { toast.error('That change didn\'t save — try again.'); }
    finally { setBusy(false); }
  };

  const onSetHome = (userId: number, jobId: number | null) =>
    run(() => setHomeJob(userId, jobId), 'Home job updated');

  const onAssign = (userId: number, date: string, jobId: number) =>
    run(() => createOverride({
      user_id: userId, job_id: jobId, date,
      end_date: repeatUntil && repeatUntil > date ? repeatUntil : undefined,
    }), 'Assigned');

  const onClear = (scheduleId: number, userId: number) =>
    run(() => deleteOverride(scheduleId, userId), 'Override cleared');

  const dayLabel = (d: string) => {
    const [y, m, dd] = d.split('-').map(Number);
    const dt = new Date(y, m - 1, dd);
    return { dow: dt.toLocaleDateString('en-CA', { weekday: 'short' }), day: dd, weekend: dt.getDay() === 0 || dt.getDay() === 6 };
  };

  // Per-employee summary: days on an override vs home this window
  const summary = useMemo(() => {
    const m = new Map<number, { assigned: number; off: number }>();
    for (const emp of data?.employees ?? []) {
      let assigned = 0;
      for (const d of data!.days) if (emp.cells[d]) assigned++;
      const off = Object.keys(emp.timeOff ?? {}).length;
      m.set(emp.user_id, { assigned, off });
    }
    return m;
  }, [data]);

  return (
    <MainLayout noPadding>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="p-4 md:p-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Scheduling</h1>
            <p className="text-sm text-gray-500">Attribute a home job, then override specific days. Overrides revert to the home job before & after.</p>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <Link to="/calendar" className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-1">
              <span className="material-symbols-outlined text-base">calendar_month</span> Calendar
            </Link>
            <Link to="/planner" className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-1">
              <span className="material-symbols-outlined text-base">view_kanban</span> Planner
            </Link>
            <span className="mx-1 h-6 w-px bg-gray-200" />
            <button onClick={() => setWeeks(w => (w === 1 ? 2 : 1))}
              className={`px-3 py-2 rounded-lg border text-sm font-medium ${weeks === 2 ? 'border-primary/40 bg-primary/10 text-primary' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
              {weeks === 1 ? '2-week view' : '1-week view'}
            </button>
            <button onClick={() => shiftWeek(-1)} className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50" title="Previous week">
              <span className="material-symbols-outlined text-base">chevron_left</span>
            </button>
            <button onClick={() => setCursor(weekStart(new Date()))} className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50">This week</button>
            <button onClick={() => shiftWeek(1)} className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50" title="Next week">
              <span className="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mb-3 text-xs text-gray-500 flex-wrap">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gray-100 border border-gray-200" /> Home job</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-primary/20 border border-primary/40" /> Override</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-200" /> Time off</span>
          <span className="flex items-center gap-1.5"><span className="material-symbols-outlined text-sm text-gray-300">add</span> Unassigned</span>
          {busy && <span className="text-primary font-semibold ml-auto">Saving…</span>}
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-400">Loading schedule…</div>
        ) : !data || data.employees.length === 0 ? (
          <div className="py-16 text-center text-gray-500">
            No active employees with portal accounts yet. Invite employees to schedule them.
          </div>
        ) : (
          <div className={`overflow-x-auto rounded-xl border border-gray-200 bg-white ${busy ? 'opacity-70 pointer-events-none' : ''}`}>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="sticky left-0 bg-gray-50 text-left px-4 py-3 font-semibold text-gray-700 min-w-[220px] z-10">Employee · Home job</th>
                  {data.days.map(d => {
                    const { dow, day, weekend } = dayLabel(d);
                    const wx = forecast.get(d);
                    return (
                      <th key={d} className={`px-2 py-2 text-center font-semibold min-w-[110px] ${weekend ? 'bg-gray-100/70' : ''} ${d === today ? 'text-primary' : 'text-gray-600'}`}>
                        <div className="text-[10px] uppercase tracking-wide">{dow}</div>
                        <div className={`text-base ${d === today ? 'text-primary' : 'text-gray-900'}`}>{day}</div>
                        {wx && (
                          <div className={`flex items-center justify-center gap-0.5 text-[10px] font-normal ${wx.risk === 'high' ? 'text-critical' : wx.risk === 'caution' ? 'text-warning' : 'text-gray-400'}`}
                            title={`${wx.t_max}°/${wx.t_min}° · ${wx.precip_prob}% precip`}>
                            <span className="material-symbols-outlined text-[12px] leading-none">{weatherIcon(wx.code)}</span>
                            {wx.t_max}°
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.employees.map(emp => (
                  <tr key={emp.user_id} className="border-b border-gray-100 last:border-0">
                    {/* Employee + home job */}
                    <td className="sticky left-0 bg-white px-4 py-3 align-top z-10 border-r border-gray-100">
                      <Link to={employeePath(emp.employee_id)} className="font-medium text-gray-900 hover:text-primary">{emp.name}</Link>
                      <p className="text-[10px] text-gray-400">
                        {summary.get(emp.user_id)?.assigned ?? 0}/{data.days.length} days scheduled
                        {(summary.get(emp.user_id)?.off ?? 0) > 0 ? ` · ${summary.get(emp.user_id)!.off} off` : ''}
                      </p>
                      <select
                        value={emp.homeJobId ?? ''}
                        onChange={e => onSetHome(emp.user_id, e.target.value ? Number(e.target.value) : null)}
                        className="mt-1 w-full text-xs border border-gray-200 rounded-lg px-2 py-1 bg-gray-50 text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary"
                      >
                        <option value="">No home job</option>
                        {data.jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
                      </select>
                    </td>

                    {/* Day cells */}
                    {data.days.map(d => {
                      const cell = emp.cells[d];
                      const isOff = !!emp.timeOff?.[d];
                      const { weekend } = dayLabel(d);
                      const key = `${emp.user_id}_${d}`;
                      const isEditing = editing === key;
                      return (
                        <td key={d} className={`px-1.5 py-1.5 text-center align-top ${weekend ? 'bg-gray-50/70' : ''}`}>
                          {isEditing ? (
                            <div className="flex flex-col gap-1">
                              <select
                                autoFocus
                                defaultValue=""
                                onChange={e => e.target.value && onAssign(emp.user_id, d, Number(e.target.value))}
                                className="w-full text-xs border border-primary rounded-lg px-1.5 py-1 focus:outline-none"
                              >
                                <option value="" disabled>Assign job…</option>
                                {data.jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
                              </select>
                              <label className="text-[10px] text-gray-400 flex items-center gap-1 justify-center">
                                until
                                <input type="date" value={repeatUntil} min={d} onChange={e => setRepeatUntil(e.target.value)}
                                  className="border border-gray-200 rounded px-1 py-0.5 text-[10px]" />
                              </label>
                              {cell?.source === 'override' && cell.scheduleId && (
                                <button onClick={() => onClear(cell.scheduleId!, emp.user_id)} className="text-[11px] text-red-500 hover:underline">Clear override</button>
                              )}
                              <button onClick={() => { setEditing(null); setRepeatUntil(''); }} className="text-[11px] text-gray-400 hover:underline">Cancel</button>
                            </div>
                          ) : isOff ? (
                            <div className="w-full rounded-lg px-2 py-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-700 font-medium" title="Approved time off">
                              Time off
                            </div>
                          ) : cell ? (
                            <button
                              onClick={() => setEditing(key)}
                              className={`w-full rounded-lg px-2 py-1.5 text-xs leading-tight truncate transition-colors border ${
                                cell.source === 'override'
                                  ? 'font-semibold'
                                  : 'text-gray-600 hover:brightness-95'
                              }`}
                              style={{
                                backgroundColor: `hsl(${jobHue(cell.jobId)} 55% ${cell.source === 'override' ? '90%' : '95%'})`,
                                borderColor: `hsl(${jobHue(cell.jobId)} 45% ${cell.source === 'override' ? '70%' : '88%'})`,
                                color: `hsl(${jobHue(cell.jobId)} 55% 30%)`,
                              }}
                              title={`${cell.jobTitle} (${cell.source})`}
                            >
                              {cell.jobTitle}
                            </button>
                          ) : (
                            <button
                              onClick={() => setEditing(key)}
                              className="w-full rounded-lg px-2 py-1.5 text-gray-300 hover:bg-gray-50 hover:text-primary"
                              title="Assign a job"
                            >
                              <span className="material-symbols-outlined text-base">add</span>
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MainLayout>
  );
};
